import { ApiError } from '../core/api/http'
import { isIsoDate, nowStamp, today } from '../core/format/dates'
import { euro, fromCents, toCents } from '../core/format/currency'
import { hasStarted } from '../core/rules/agenda'
import { balance, codeLast4 } from '../core/rules/ledger'
import { isKuurExpired, kuurRefund, kuurStopProblem, prepaidLeft, suggestedRefundCents } from '../core/rules/kuur'
import { vatRateOf } from '../core/rules/revenue'
import { resolveFeatures } from '../core/rules/features'
import {
  MAX_SALE_LINES,
  NO_BRAND_DISCOUNT,
  brandAllowsDiscount,
  cashProblem,
  changeCents,
  closeProblem,
  kassaSummary,
  lineProblem,
  lineTotalCents,
  nextCreditNoteNumber,
  nextSaleNumber,
  creditCentsFor,
  saleTotalCents,
  splitProblem,
  voidRefundOf,
  voucherCentsFor,
} from '../core/rules/kassa'
import { localCatalog } from '../data/catalog'
import { customerName } from '../admin/customers/customerRules'
import { db, type AppointmentRow, type KuurStopRow, type SaleRow } from './db'
import { creditNoteNumbers, demoSaleReturnBackend, returnsOf, returnsOfSale } from './saleReturnBackend'
import { productOf } from './pricing'
import { demoId, reply } from './reply'
import { toAgenda } from './agendaBackend'
import { demoAdminIdentity } from './adminIdentity'
import { kassaDayFor } from '../core/rules/staffAccess'
import { bookSaleStock } from './stockBackend'
import { attachKuur, canAttachKuur } from './kuurOrders'
import {
  cancelKassaVouchers,
  canCancelKassaVouchers,
  creditBalanceCents,
  issueKassaVouchers,
  issueRefundCredit,
  redeemCredit,
  redeemVoucher,
  restoreCredit,
  restoreVoucher,
  salonVoucher,
} from './voucherBackend'
import { recordCustomerSource } from './acquisition'
import { normalizeSource } from '../core/rules/acquisition'
import type { AdminBackend } from '../core/types/backend'
import type { KassaAppointment, KassaDay, KuurStop, KuurStopRequest, Sale, SaleLine, SaleLineInput, SaleRequest } from '../core/types/kassa'

/**
 * DEMO — de kassa aan de kant van de server (docs/KASSA-ABONNEMENTEN.md §3). Dezelfde regels als
 * de echte server (core/rules/kassa.ts). Eén bon is in één keer betaald; corrigeren kan alleen op de
 * dag zelf, vóór de dagafsluiting.
 */

function fail(message: string, status: number, code?: string, field?: string): never {
  throw new ApiError(message, status, field ? { [field]: message } : undefined, code)
}

/** Een bon zoals de server hem geeft: zonder interne velden, met zijn retouren (oudste eerst). */
const strip = ({ requestId: _r, voidRequestId: _v, voucherId: _id, acquisitionSource: _src, ...s }: SaleRow): Sale => {
  const returns = returnsOfSale(s.id)
  return returns.length ? { ...s, returns } : s
}

const salesOf = (day: string) =>
  db
    .sales()
    .filter((s) => s.day === day)
    .sort((a, b) => b.at.localeCompare(a.at))

const productLines = (lines: readonly SaleLineInput[]) => lines.flatMap((l) => (l.kind === 'product' ? [{ productId: l.productId, qty: l.qty }] : []))

const isOpenDay = (day: string) => day === today() && !db.cashDay(day)

/** Naam, catalogusprijs en btw van een regel, of een 422 als hij niet (meer) bestaat. */
function priced(l: SaleLineInput): SaleLine {
  if (l.kind === 'giftcard') {
    if (l.validFor !== 'all' && l.validFor !== 'treatments') return fail('Kies waarvoor de cadeaubon geldt.', 422, 'line_invalid', 'lines')
    const name = l.validFor === 'treatments' ? 'Cadeaubon (behandelingen)' : 'Cadeaubon'
    // Geen catalogusprijs: de waarde is wat Sarah invult. 0% btw: btw pas bij inwisselen.
    return { ...l, name, listPriceCents: l.priceCents, vatRate: 0, totalCents: lineTotalCents(l) }
  }
  if (l.kind === 'product') {
    const p = productOf(l.productId)
    if (!p || p.kind === 'giftcard' || p.kind === 'kuur') return fail('Dit product bestaat niet (meer).', 422, 'unknown_product', 'lines')
    return { ...l, name: p.name, listPriceCents: toCents(p.price), vatRate: vatRateOf(p), totalCents: lineTotalCents(l) }
  }
  const t = localCatalog.treatments.find((x) => x.slug === l.treatmentSlug && !x.online)
  if (!t) return fail('Deze behandeling bestaat niet (meer).', 422, 'unknown_treatment', 'lines')
  if (l.kind === 'kuur') {
    if (!t.kuur || t.kuur.price <= 0) return fail(`${t.name} heeft geen kuur.`, 422, 'no_kuur', 'lines')
    return { ...l, name: `Kuur ${t.name} · ${t.kuur.sessions}×`, listPriceCents: toCents(t.kuur.price), vatRate: 21, totalCents: lineTotalCents(l) }
  }
  // Zonder losse prijs (alleen als kuur): geen catalogusprijs om korting tegen te zetten.
  return { ...l, name: t.name, listPriceCents: t.price === null ? 0 : toCents(t.price), vatRate: 21, totalCents: lineTotalCents(l) }
}

function appointmentFor(req: SaleRequest): AppointmentRow | null {
  if (!req.appointmentId) return null
  const row = db.appointments().find((a) => a.id === req.appointmentId)
  if (!row) return fail('Deze afspraak bestaat niet meer.', 404)
  if (row.date !== req.day) return fail('Deze afspraak is op een andere dag.', 422, 'wrong_day')
  if (row.outcome === 'geannuleerd' || row.outcome === 'no-show') return fail('Deze afspraak is geannuleerd of niet doorgegaan.', 409, 'appointment_closed')
  if (row.saleId) return fail('Deze afspraak is al afgerekend.', 409, 'already_paid')
  if (row.customerId && req.customerId && row.customerId !== req.customerId) return fail('De klant past niet bij de afspraak.', 422, 'customer_mismatch')
  // Een collega rekent alleen haar eigen afspraken af (zoals de server: 403).
  if (!demoAdminIdentity.can('kassa.sell', { staffId: row.staffId ?? 'sarah' })) return fail('Deze afspraak is niet van jou.', 403, 'not_your_appointment')
  return row
}

/** "Uit kuur" mag alleen bij een kuurafspraak met een geldige vooruitbetaalde kuur (of een kuur op deze bon). */
function checkFromKuur(req: SaleRequest, appointment: AppointmentRow | null) {
  const lines = req.lines.filter((l) => l.kind === 'treatment' && l.fromKuur)
  if (lines.length === 0) return
  if (lines.length > 1) fail('Eén sessie uit de kuur per bon.', 422, 'kuur_session', 'lines')
  const line = lines[0]
  if (line.kind !== 'treatment') return
  if (!appointment || appointment.treatmentSlug !== line.treatmentSlug) {
    fail('"Uit kuur" kan alleen bij de afspraak zelf: plan de sessie in de agenda, dan telt hij af bij afronden.', 422, 'kuur_session', 'lines')
  }
  // Kuur op deze bon gekocht: deze behandeling is sessie 1.
  if (req.lines.some((l) => l.kind === 'kuur' && l.treatmentSlug === line.treatmentSlug)) return
  const course = req.customerId ? db.account(req.customerId)?.course : null
  const now = today()
  const ok =
    course?.prepaid &&
    course.treatmentSlug === line.treatmentSlug &&
    !isKuurExpired(course, now) &&
    (prepaidLeft(course, now) > 0 || appointment?.outcome === 'afgerond')
  if (!ok) fail('Er is geen geldige vooruitbetaalde kuur voor deze behandeling. Reken de behandeling af.', 422, 'kuur_session', 'lines')
}

function checkKuurPurchase(req: SaleRequest) {
  const kuren = req.lines.filter((l): l is Extract<SaleLineInput, { kind: 'kuur' }> => l.kind === 'kuur')
  if (kuren.length === 0) return
  if (!resolveFeatures(db.savedFeatures()).prepaidKuur) fail('Kuur vooruit betalen staat uit (Instellingen › Koppelingen & privacy).', 422, 'kuur_off', 'lines')
  if (kuren.length > 1) fail('Eén kuur per bon.', 422, 'kuur_purchase', 'lines')
  if (!req.customerId) fail('Een kuur komt op het account van de klant: kies eerst de klant.', 422, 'kuur_needs_customer', 'lines')
  const account = req.customerId ? db.account(req.customerId) : null
  if (account && !canAttachKuur(account.course, kuren[0].treatmentSlug)) {
    fail('Deze klant heeft al een andere lopende kuur. Rond die af of beëindig hem in het dossier.', 409, 'kuur_conflict', 'lines')
  }
}

function toSettle(day: string): KassaAppointment[] {
  return db
    .appointments()
    .filter((r) => r.date === day && r.kind === 'salon' && !r.saleId && r.outcome !== 'geannuleerd' && r.outcome !== 'no-show' && r.status === 'bevestigd')
    .sort((a, b) => a.time.localeCompare(b.time))
    .flatMap((r) => {
      const a = toAgenda(r)
      if (!a) return []
      return [
        {
          id: a.id,
          time: a.time,
          treatmentSlug: a.treatmentSlug,
          ...(a.customer.id ? { customerId: a.customer.id } : {}),
          customerName: a.customer.name,
          finished: a.outcome === 'afgerond',
          started: hasStarted(a),
          ...(a.session ? { session: a.session } : {}),
          ...(a.customer.kuur ? { kuur: a.customer.kuur } : {}),
          staffId: a.staffId,
          ...(a.products?.advice.length ? { adviceProductIds: a.products.advice } : {}),
        },
      ]
    })
}

const stripStop = ({ requestId: _r, ...s }: KuurStopRow): KuurStop => s

/** Kuren die op `day` met pin of contant zijn terugbetaald: die gaan door de kassa. */
const refundsOf = (day: string) =>
  db
    .kuurStops()
    .filter((k) => k.day === day && k.method !== 'tegoed' && k.refundCents > 0)
    .sort((a, b) => b.at.localeCompare(a.at))
    .map(stripStop)

function dayView(day: string): KassaDay {
  const sales = salesOf(day).map(strip)
  const kuurRefunds = refundsOf(day)
  const returns = returnsOf(day)
  const closed = db.cashDay(day)
  return {
    day,
    editable: isOpenDay(day),
    sales,
    toSettle: toSettle(day),
    closed: closed ? (({ requestId: _r, ...c }) => c)(closed) : null,
    summary: kassaSummary(sales, kuurRefunds, returns),
    kuurRefunds,
    returns,
  }
}

/**
 * Een vooruitbetaalde kuur stoppen en terugbetalen (docs/KASSA-ABONNEMENTEN.md §4.4). De server
 * rekent het voorstel zelf; een ander bedrag mag met een reden. Pin of contant gaat door de kassa
 * van vandaag, tegoed komt op het grootboek van de klant. Daarna is de kuur weg, en open afspraken
 * verliezen hun sessienummer (gewone afspraken, af te rekenen).
 */
function stopKuur(customerId: string, req: KuurStopRequest): KuurStop {
  const known = db.kuurStops().find((k) => k.requestId === req.requestId)
  if (known) return stripStop(known)
  if (demoAdminIdentity.access().grants['kassa.refund'] !== 'all') fail('Een kuur stoppen kan alleen Sarah.', 403, 'forbidden')

  const account = db.account(customerId)
  if (!account) return fail('Deze klant bestaat niet (meer).', 404)
  const course = account.course
  if (!course?.prepaid) return fail('Deze kuur is niet vooruit betaald; beëindig hem zonder terugbetaling.', 409, 'not_prepaid')
  if (req.method !== 'pin' && req.method !== 'contant' && req.method !== 'tegoed') fail('Kies hoe het geld teruggaat.', 422, 'method', 'method')

  const day = today()
  const t = localCatalog.treatments.find((x) => x.slug === course.treatmentSlug)
  const suggestedCents = suggestedRefundCents(course, t?.price ?? null, day)
  const paidCents = toCents(course.prepaid.paid)
  const reason = req.reason.trim()
  const problem = kuurStopProblem({ refundCents: req.refundCents, suggestedCents, paidCents, reason })
  if (problem) fail(problem.message, 422, 'refund_invalid', problem.field)
  // Geld via de kassa: alleen op een dag die nog openstaat (de la is anders al geteld).
  if (req.refundCents > 0 && req.method !== 'tegoed' && db.cashDay(day)) {
    fail('De kassa van vandaag is al afgesloten. Betaal terug als tegoed, of morgen via de kassa.', 409, 'day_closed', 'method')
  }

  const name = customerName(account.customer)
  const treatmentName = t?.name ?? course.treatmentSlug
  const stop: KuurStopRow = {
    id: demoId('cn').toLowerCase(),
    requestId: req.requestId,
    // Eén reeks voor alle creditnota's: gestopte kuren en retouren.
    number: nextCreditNoteNumber(creditNoteNumbers(), day),
    at: nowStamp(),
    day,
    customerId,
    customerName: name,
    treatmentSlug: course.treatmentSlug,
    treatmentName,
    sessionsTotal: course.total,
    sessionsDone: Math.min(course.done, course.total),
    paidCents,
    suggestedCents,
    refundCents: req.refundCents,
    method: req.method,
    reason,
    explanation: kuurRefund(course, t?.price ?? null, day).explanation,
    // Btw zoals bij de verkoop van een kuur (K5).
    vatRate: 21,
    createdBy: demoAdminIdentity.current().name,
    accountId: demoAdminIdentity.current().accountId,
  }
  db.addKuurStop(stop)
  if (req.method === 'tegoed') issueRefundCredit(customerId, req.refundCents, stop.id, `Kuur ${treatmentName} gestopt (${stop.number})`)
  db.changeAccount(customerId, (a) => [{ ...a, course: null }, null])
  db.appointments()
    .filter((r) => r.customerId === customerId && !r.outcome && r.session && r.treatmentSlug === course.treatmentSlug)
    .forEach((r) => db.changeAppointment(r.id, ({ session: _s, ...rest }) => rest))
  return stripStop(stop)
}

function createSale(req: SaleRequest): Sale {
  const known = db.sales().find((s) => s.requestId === req.requestId)
  if (known) return strip(known)

  if (req.day !== today()) fail('Afrekenen kan alleen voor vandaag.', 422, 'wrong_day')
  if (db.cashDay(req.day)) fail('Deze dag is al afgesloten. Er kunnen geen bonnen meer bij.', 409, 'day_closed')
  if (req.lines.length === 0) fail('Zet minstens één regel op de bon.', 422, 'empty', 'lines')
  if (req.lines.length > MAX_SALE_LINES) fail(`Hoogstens ${MAX_SALE_LINES} regels op één bon.`, 422, 'too_many', 'lines')

  const account = req.customerId ? db.account(req.customerId) : null
  if (req.customerId && !account) fail('Deze klant bestaat niet (meer).', 404)
  const appointment = appointmentFor(req)

  const lines = req.lines.map(priced)
  lines.forEach((l) => {
    const brand = l.kind === 'product' ? (productOf(l.productId)?.brand ?? '') : ''
    const problem = lineProblem(l, l.listPriceCents, l.kind === 'product' ? { discountAllowed: brandAllowsDiscount(db.savedBrands() ?? localCatalog.brands, brand) } : {})
    if (problem) fail(`${l.name}: ${problem}`, 422, problem === NO_BRAND_DISCOUNT ? 'no_brand_discount' : 'line_invalid', 'lines')
  })
  checkKuurPurchase(req)
  checkFromKuur(req, appointment)

  const totalCents = saleTotalCents(lines)
  const voucher = req.voucherCode?.trim() ? salonVoucher(req.voucherCode) : null
  const voucherCents = voucher ? voucherCentsFor(balance(voucher.ledger, 'cents'), voucher.validFor, lines) : 0
  if (voucher && voucherCents === 0) {
    if (voucher.validFor === 'all') fail('Een cadeaubon kun je niet met een cadeaubon betalen.', 422, 'voucher_not_for_giftcard', 'voucherCode')
    fail('Deze cadeaubon geldt alleen voor behandelingen; er staat geen behandeling op de bon.', 422, 'voucher_treatments_only', 'voucherCode')
  }
  // Klanttegoed: alleen met account, hooguit het saldo en wat er na de cadeaubon nog openstaat.
  const creditCents = req.creditCents ?? 0
  if (creditCents !== 0) {
    if (!account) fail('Tegoed gebruiken kan alleen bij een klant met account.', 422, 'credit', 'creditCents')
    const max = account ? creditCentsFor(creditBalanceCents(account.customer.id), lines, voucherCents) : 0
    if (!Number.isInteger(creditCents) || creditCents < 0 || creditCents > max) {
      fail(max > 0 ? `Er kan hooguit ${euro(fromCents(max), { cents: true })} van het tegoed af.` : 'Er is geen tegoed te gebruiken op deze bon.', 422, 'credit', 'creditCents')
    }
  }
  const methodCents = totalCents - voucherCents - creditCents
  const split = req.method === 'pin' && req.cashPartCents !== undefined
  if (split) {
    const problem = splitProblem(req.cashPartCents, methodCents)
    if (problem) fail(problem, 422, 'split', 'cashPartCents')
  }
  // Wat er contant betaald moet worden: alles bij contant, het contante deel bij gesplitst.
  const cashDue = req.method === 'contant' ? methodCents : split ? (req.cashPartCents ?? 0) : 0
  if (cashDue > 0) {
    const problem = cashProblem(req.cashReceivedCents, cashDue)
    if (problem) fail(problem, 422, 'cash', 'cashReceivedCents')
  }

  const at = nowStamp()
  const id = demoId('bon').toLowerCase()
  const source = normalizeSource(req.acquisitionSource)
  const guest = appointment?.guest?.name
  const number = nextSaleNumber(db.sales().map((s) => s.number), req.day)
  const name = account ? customerName(account.customer) : req.guestName?.trim() || guest || 'Losse klant'
  // Verkochte cadeaubonnen: na alle controles, zodat een geweigerde bon nooit een code maakt.
  const issued = issueKassaVouchers(
    { number, customerName: name },
    lines.flatMap((l) => (l.kind === 'giftcard' ? [{ cents: l.priceCents, validFor: l.validFor }] : [])),
  )
  const me = demoAdminIdentity.current()
  const performer = appointment ? (appointment.staffId ?? 'sarah') : me.staffId
  const sale: SaleRow = {
    id,
    requestId: req.requestId,
    number,
    at,
    day: req.day,
    ...(account ? { customerId: account.customer.id } : {}),
    customerName: name,
    ...(issued.length ? { issuedVouchers: issued } : {}),
    ...(appointment ? { appointmentId: appointment.id } : {}),
    // Uitvoerder: van de afspraak; bij een losse bon wie hem maakte (zoals de server).
    staffId: performer,
    createdBy: me.name,
    accountId: me.accountId,
    // Per regel wie hem deed of verkocht (Omzet › Per uitvoerder, straks provisie).
    lines: lines.map((l) => (l.kind === 'treatment' || l.kind === 'kuur' ? { ...l, performerId: performer } : { ...l, sellerId: me.staffId })),
    totalCents,
    voucherCents,
    ...(voucher ? { voucherLast4: codeLast4(voucher.code), voucherId: voucher.id } : {}),
    ...(creditCents > 0 ? { creditCents } : {}),
    method: req.method,
    methodCents,
    ...(split ? { cashPartCents: cashDue } : {}),
    ...(cashDue > 0 && req.cashReceivedCents !== undefined
      ? { cashReceivedCents: req.cashReceivedCents, changeCents: changeCents(req.cashReceivedCents, cashDue) }
      : {}),
    status: 'betaald',
    ...(source ? { acquisitionSource: source } : {}),
  }
  db.putSale(sale)
  // Alleen de eerste herkomst komt bij de klant; een ongeldig antwoord doet niets.
  if (account && source) recordCustomerSource(account.customer.id, source, 'kassa')

  if (voucher) redeemVoucher(voucher.id, voucherCents, id)
  if (account && creditCents > 0) redeemCredit(account.customer.id, creditCents, id, `Gebruikt op bon ${number}`)
  // Van de salonvoorraad. Op 0 gaat de verkoop door (de telling kan fout zijn); de stand wordt dan negatief.
  bookSaleStock(productLines(lines), 'salon', -1, sale.number)

  // Een kuur op de bon komt op het account; staat de behandeling van vandaag erop als "uit kuur", dan is dat sessie 1.
  const kuur = lines.find((l) => l.kind === 'kuur')
  if (kuur && kuur.kind === 'kuur' && account) {
    attachKuur(account.customer.id, kuur.treatmentSlug, kuur.priceCents / 100)
    const firstSession = lines.some((l) => l.kind === 'treatment' && l.fromKuur && l.treatmentSlug === kuur.treatmentSlug)
    if (firstSession && appointment) {
      if (appointment.outcome === 'afgerond') db.changeAccount(account.customer.id, (a) => [{ ...a, course: a.course ? { ...a.course, done: 1 } : a.course }, null])
      else db.changeAppointment(appointment.id, (r) => ({ ...r, session: 1 }))
    }
  }
  if (appointment) db.changeAppointment(appointment.id, (r) => ({ ...r, saleId: id }))
  return strip(sale)
}

export const demoKassaBackend: Pick<AdminBackend, 'fetchKassaDay' | 'createSale' | 'voidSale' | 'returnSale' | 'closeKassaDay' | 'fetchCashDayCloses' | 'stopKuur'> = {
  ...demoSaleReturnBackend,

  async stopKuur(customerId, req) {
    try {
      return await reply(stopKuur(customerId, req), 500)
    } catch (err) {
      await reply(null, 300)
      throw err
    }
  },

  async fetchCashDayCloses(from, to) {
    if (demoAdminIdentity.access().grants['kassa.totals'] !== 'all') throw new ApiError('Dit deel is alleen voor Sarah.', 403, undefined, 'forbidden')
    const list = db
      .cashDays()
      .filter((d) => d.day >= from && d.day <= to)
      .sort((a, b) => b.day.localeCompare(a.day))
      .map(({ requestId: _r, ...c }) => c)
    return reply(list, 250)
  },

  fetchKassaDay(day) {
    if (!isIsoDate(day)) throw new ApiError('Onbekende datum.', 422)
    // Zoals de server: een collega krijgt alleen haar eigen rijen en geen dagtotalen mee.
    const { totalsHidden: _hidden, ...view } = kassaDayFor(dayView(day), demoAdminIdentity.viewer('kassa.totals'))
    return reply(view, 250)
  },

  async createSale(req) {
    // Eerst rekenen en valideren, dan pas "netwerk": een fout komt als 422/409 terug, net als bij de server.
    try {
      return await reply(createSale(req), 500)
    } catch (err) {
      await reply(null, 300)
      throw err
    }
  },

  async voidSale(id, req) {
    const row = db.sales().find((s) => s.id === id)
    if (!row) throw new ApiError('Deze bon bestaat niet.', 404)
    // Zoals de server: een collega corrigeert alleen haar eigen bonnen (ze ziet ook alleen die).
    if (!demoAdminIdentity.can('kassa.void', { staffId: row.staffId ?? 'sarah' })) throw new ApiError('Deze bon is niet van jou. Vraag Sarah om hem te corrigeren.', 403, undefined, 'not_your_sale')
    if (row.status === 'gecorrigeerd') {
      if (row.voidRequestId === req.requestId) return reply(strip(row), 300)
      throw new ApiError('Deze bon is al gecorrigeerd.', 409, undefined, 'already_voided')
    }
    if (!isOpenDay(row.day)) throw new ApiError('Corrigeren kan alleen op de dag zelf, vóór de dagafsluiting. Neem terug wat terugkomt met Retour.', 409, undefined, 'too_late')
    if (db.saleReturns().some((x) => x.saleId === row.id)) {
      throw new ApiError('Er staat al een retour op deze bon. Neem de rest ook terug met Retour.', 409, undefined, 'has_returns')
    }
    if (row.lines.some((l) => l.kind === 'kuur')) {
      throw new ApiError('Er zit een kuur op deze bon. Stop de kuur in het dossier (Kuur stoppen en terugbetalen) in plaats van de bon te corrigeren.', 409, undefined, 'kuur_in_sale')
    }
    const issuedIds = (row.issuedVouchers ?? []).map((v) => v.id)
    if (!canCancelKassaVouchers(issuedIds)) {
      throw new ApiError('Een cadeaubon van deze bon is al (deels) gebruikt. Corrigeer het saldo bij Cadeaubonnen in plaats van de bon.', 409, undefined, 'voucher_used')
    }
    const reason = req.reason.trim()
    if (!reason) throw new ApiError('Geef een reden.', 422, { reason: 'Geef een reden.' })
    // Kwam er geld binnen, dan legt de server alleen vast wat Sarah bevestigt terug te geven.
    const refunded = voidRefundOf(row)
    if (refunded.pinCents + refunded.cashCents > 0 && !req.refundGiven) {
      throw new ApiError('Bevestig dat je het geld hebt teruggegeven.', 422, { refundGiven: 'Bevestig dat je het geld hebt teruggegeven.' }, 'refund_unconfirmed')
    }
    cancelKassaVouchers(issuedIds, `Bon ${row.number} gecorrigeerd: ${reason}`)
    restoreVoucher(row.id, `Bon ${row.number} gecorrigeerd: ${reason}`)
    if (row.customerId && row.creditCents) restoreCredit(row.customerId, row.id, `Bon ${row.number} gecorrigeerd: ${reason}`)
    bookSaleStock(productLines(row.lines), 'salon', 1, row.number, `Bon gecorrigeerd: ${reason}`)
    if (row.appointmentId) db.changeAppointment(row.appointmentId, ({ saleId: _s, ...r }) => r)
    const updated: SaleRow = { ...row, status: 'gecorrigeerd', voidRequestId: req.requestId, correction: { at: nowStamp(), reason, ...(refunded.pinCents + refunded.cashCents > 0 ? { refunded } : {}) } }
    db.putSale(updated)
    return reply(strip(updated), 400)
  },

  async closeKassaDay(day, req) {
    const known = db.cashDay(day)
    if (known) {
      if (known.requestId === req.requestId) return reply(dayView(day), 300)
      throw new ApiError('Deze dag is al afgesloten.', 409, undefined, 'day_closed')
    }
    if (!isIsoDate(day) || day > today()) throw new ApiError('Een dag in de toekomst kun je niet afsluiten.', 422)
    const expected = kassaSummary(salesOf(day).map(strip), refundsOf(day), returnsOf(day)).expectedCashCents
    // Een collega telt blind (ze ziet het verwachte bedrag niet): een verschil zonder notitie mag,
    // Sarah ziet het in haar dagoverzicht. Sarah zelf moet een verschil toelichten.
    const who = demoAdminIdentity.current()
    const blind = demoAdminIdentity.access().grants['kassa.totals'] !== 'all'
    const problem = blind ? (req.countedCashCents < 0 ? 'Vul in wat er in de la zit.' : null) : closeProblem(req.countedCashCents, expected, req.note)
    if (problem) throw new ApiError(problem, 422, { countedCashCents: problem })
    db.putCashDay({
      day,
      requestId: req.requestId,
      closedAt: nowStamp(),
      expectedCashCents: expected,
      countedCashCents: req.countedCashCents,
      differenceCents: req.countedCashCents - expected,
      note: req.note.trim(),
      closedBy: who,
    })
    return reply(dayView(day), 400)
  },
}
